import { Grid, Typography } from '@mui/material';
import { PageSelector, useAgGridPagination } from '@tassat/ag-grid';
import { ToolbarButton } from '@tassat/button';
import {
    ActionTypes,
    ClientMessagesTableOptions,
    clientMessagesTableOptionsInitialState
} from '@tassat/common-definitions';
import { DateRangeSelector } from '@tassat/formik-input';
import { ClientMessagesForm } from '@tassat/forms';
import { ClientMessageRecord } from '@tassat/fred-definitions';
import { Export as ExportIcon } from '@tassat/icon';
import { FormModal } from '@tassat/modal';
import moment from 'moment';
import React from 'react';

import { submitClientMessage } from 'Containers/admin/forms/helpers/clientMessagesFormFn';
import ClientMessagesAgGrid from 'Containers/admin/pages/clientMessages/clientMessagesGrid';
import Header from 'Containers/header';
import { CLIENTMESSAGES_PER_EXPORT } from 'Lib/constant';
import { exportClientMessages } from 'Lib/exports/clientMessageExports';
import Worker from 'Lib/worker/PrimusWorkerWrapper';
import { useAppSelector } from 'Reducers/store/hooks';
import { getMessagesForValidation } from 'Selectors/clientMessages';
import { TableOptionsContext, tableOptionsReducer } from 'SharedComponents/contexts/TableOptionsContext';
import { useExports } from 'SharedComponents/hooks/useExports';
import { PageHeaderTitle } from 'SharedComponents/pageHeader';

import type { ClientMessagesProps } from '.';

export function ClientMessages(props: ClientMessagesProps) {
    const { classes } = props;

    const validationMessages = useAppSelector((state) => getMessagesForValidation(state, 'StartAt'));
    const [ clientMessagesFormOpen, setClientMessagesFormOpen ] = React.useState<boolean>(false);
    const [ clientMessagesFormType, setClientMessagesFormType ] = React.useState<ActionTypes>(ActionTypes.add);
    const [ selectedClientMessage, setEditClientMessage ] = React.useState<ClientMessageRecord | undefined>(undefined);
    const editClientMessage = (clientMessage: ClientMessageRecord) => {
        setClientMessagesFormType(ActionTypes.edit);
        setEditClientMessage(clientMessage);
        setClientMessagesFormOpen(true);
    };

    const [ TableOptions, TableOptionsDispatch ] = React.useReducer<(TableOptions, { type, values }:
    { type: string; values: object }) => ClientMessagesTableOptions>(tableOptionsReducer, clientMessagesTableOptionsInitialState);
    const tableContextMemo = React.useMemo(() => {
        return {
            TableOptions,
            TableOptionsDispatch
        };
    }, [ TableOptions, TableOptionsDispatch ]);
    const [setPageNum] = useAgGridPagination(TableOptionsDispatch);
    const [ rowCount, setRowCount ] = React.useState<number>(0);
    const {
        exportError,
        exportInFlight,
        setExportsContext
    } = useExports();

    React.useEffect(() => {
        fetchMessages();
    }, [
        TableOptions.DateCondition,
        TableOptions.FilterCondition,
        TableOptions.PageNo,
        TableOptions.Sort
    ]);

    const fetchMessages = async (pageNum?) => {
        const {
            PageNo,
            RecordPerPage,
            Sort,
            DateCondition,
            FilterCondition
        } = TableOptions;

        const res = await Worker.getInstance()
            .getClientMessages({
                Condition: `${FilterCondition && `${FilterCondition}${DateCondition ? ':' : ''}`}${DateCondition}`,
                Sort,
                PageNo: pageNum || PageNo,
                RecordPerPage
            }) || { data: undefined };

        setRowCount(res.data && res.data.TotalCount);

        return res;
    };

    const fetchMessagesExport = async () => {
        const {
            Sort,
            DateCondition,
            FilterCondition
        } = TableOptions;

        let query = `&Condition=${`${FilterCondition && `${FilterCondition}${DateCondition ? ':' : ''}`}${DateCondition}`}`;
        query += `&Sort=${Sort}&RecordPerPage=${CLIENTMESSAGES_PER_EXPORT}`;

        setExportsContext((exportsContext) => ({
            ...exportsContext,
            totalChunks: Math.floor(rowCount / CLIENTMESSAGES_PER_EXPORT) + 1,
            chunkCounter: 0,
            exportInFlight: true,
            RecordType: 'clientmessage',
            filterQuery: query
        }));

        const res = await Worker.getInstance()
            .getClientMessages({
                Condition: `${FilterCondition && `${FilterCondition}${DateCondition ? ':' : ''}`}${DateCondition}`,
                Sort,
                RecordPerPage: CLIENTMESSAGES_PER_EXPORT,
                Export: true
            }) || { data: undefined };

        exportClientMessages(res.data.clientmessages);
    };

    const selectTimeRange = (startDate: string | moment.Moment, endDate: string | moment.Moment, field?: 'StartAt' | 'EndAt' | '') => {
        if (moment.isMoment(startDate) && moment.isMoment(endDate)) {
            TableOptionsDispatch({
                type: 'update',
                values: {
                    SelectedDateFilter: 'customRange',
                    SelectedDateField: field,
                    DateCondition: `${field}|GT|${startDate.valueOf()}:${field}|LT|${endDate.valueOf()}`,
                    PageNo: 1,
                    SelectedFilter: 'none',
                    TableFilters: [],
                    FilterCondition: ''
                }
            });
        } else return;
    };

    const selectFilter = (filterType: 'pending' | 'none') => {
        if (filterType === 'pending') {
            TableOptionsDispatch({
                type: 'update',
                values: {
                    SelectedDateFilter: '',
                    SelectedDateField: '',
                    DateCondition: '',
                    PageNo: 1,
                    SelectedFilter: 'pending',
                    TableFilter: [],
                    FilterCondition: 'Status|EQ|54'
                }
            });
        } else {
            TableOptionsDispatch({
                type: 'update',
                values: clientMessagesTableOptionsInitialState
            });
        }
    };

    return (
        <TableOptionsContext.Provider value={tableContextMemo}>
            <Grid
                className={classes.root}
                container
                direction='column'
                spacing={0}
                wrap='nowrap'
            >
                <Header />
                <PageHeaderTitle
                    form={true}
                    onClick={() => {
                        setClientMessagesFormOpen(true);
                    }}
                    title='Messages'
                />
                <Grid
                    className={classes.gridContainer}
                    container
                    direction='column'
                    spacing={0}
                    wrap='nowrap'
                >
                    <Grid
                        className={classes.toolbarContainer}
                        container
                        direction='row'
                        spacing={0}
                        wrap='nowrap'
                    >
                        <Grid
                            alignItems='center'
                            container
                            direction='row'
                            item
                            spacing={0}
                            wrap='nowrap'
                            xs={8}
                        >
                            <Typography
                                className={classes.dateRangeSelectorLabel}
                                variant='body1'
                            >Start Date/Time
                            </Typography>
                            <DateRangeSelector
                                allowSingleDay
                                field='StartAt'
                                selectedDateField={TableOptions.SelectedDateField}
                                selectTimeRange={(startDate, endDate, field) => selectTimeRange(startDate, endDate, field as 'StartAt' | 'EndAt' | '')}
                                timeFrameInDays={TableOptions.SelectedDateFilter}
                            />
                            <Typography
                                className={classes.dateRangeSelectorLabel}
                                variant='body1'
                            >End Date/Time
                            </Typography>
                            <DateRangeSelector
                                allowSingleDay
                                field='EndAt'
                                selectedDateField={TableOptions.SelectedDateField}
                                selectTimeRange={(startDate, endDate, field) => selectTimeRange(startDate, endDate, field as 'StartAt' | 'EndAt' | '')}
                                timeFrameInDays={TableOptions.SelectedDateFilter}
                            />
                            <Typography
                                className={`${classes.filterOption} ${TableOptions.SelectedFilter === 'pending' ? classes.selectedFilterOption : ''}`}
                                onClick={() => selectFilter('pending')}
                                variant='h5'
                            >
                                Pending
                            </Typography>
                            <Typography
                                className={classes.clearFiltersButton}
                                onClick={() => selectFilter('none')}
                                variant='h5'
                            >
                                Clear Filters
                            </Typography>
                        </Grid>
                        <Grid
                            alignItems='center'
                            className={classes.pageSelectorContainer}
                            container
                            direction='row'
                            item
                            justifyContent='flex-end'
                            spacing={0}
                            wrap='nowrap'
                            xs={4}
                        >
                            <PageSelector
                                changePageOnApplyOnly
                                currentPageNum={TableOptions.PageNo}
                                onPageChange={setPageNum}
                                totalRows={rowCount}
                            />
                            <ToolbarButton
                                actionInProgress={exportInFlight}
                                className={classes.exportButton}
                                Icon={ExportIcon}
                                iconClassName={classes.exportIcon}
                                onClick={fetchMessagesExport}
                                text='Export'
                            />
                        </Grid>
                    </Grid>
                    <ClientMessagesAgGrid
                        editClientMessage={editClientMessage}
                        tableOptions={TableOptions}
                    />
                </Grid>
                <FormModal
                    Form={ClientMessagesForm}
                    formProps={{
                        actionType: clientMessagesFormType,
                        editMessage: selectedClientMessage,
                        validationMessages: validationMessages,
                        submitClientMessage: submitClientMessage
                    }}
                    onClose={() => {
                        setClientMessagesFormOpen(false);
                        setClientMessagesFormType(ActionTypes.add);
                    }}
                    open={clientMessagesFormOpen}
                    title='Create Client Message'
                />
            </Grid>
        </TableOptionsContext.Provider>
    );
}